import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useFormApi } from '@lamesarv-sdk/hooks';
import { navigate } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useDevice } from '@/hooks/useDevice';
import { withSuspense } from '@/tools/withSuspense';

import { RichText } from '@lamesarv-sdk/components';
import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';

import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFormMapping } from '@/types/dynamicsForms';
import { FormTag } from '@/types/forms';
import { parsePhone } from '@/tools/dynamics';
import { IFormProps, IFormResetterProps } from '../types';

import { BaseOptInCheckbox, BaseSubmit } from '../base';
import { IFormSchema, validationSchema } from './schema';

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '', mobile: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormStayUpdated = withSuspense((props: IFormProps) => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { env } = useEnv();

  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();

  const onSubmit = async (data: IFormSchema, metadata?: Record<string, unknown>) => {
    const formInput = { ...data, extra_tag_ids: [FormTag.STAY_UPDATED] };
    const formMappings: IDynamicsFormMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: String(DynamicsLeadType.marketing),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      {
        FormFieldName: 'firstName',
        DataverseFieldName: 'firstname',
      },
      {
        FormFieldName: 'lastName',
        DataverseFieldName: 'lastname',
      },
      {
        DataverseFieldName: 'mobilephone',
        DataverseFieldValue: parsePhone(formInput.phone),
      },
      {
        FormFieldName: 'email',
        DataverseFieldName: 'emailaddress1',
      },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      formInput.ip = device.ip;
      formInput.user_agent = device.userAgent;
      formInput.platform = device.platform;
      formMappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      formInput.geo_coordinates = layoutContext.geoLocation.join(',');
      formMappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      formInput.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      formInput.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
      formMappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: formInput.captcha_score,
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: formInput.captcha_threshold,
        },
      );
    }

    if (env.ENABLE_FORMS_LEGACY) {
      sendFormLegacy(formInput);
    }

    if (env.ENABLE_FORMS_DYNAMICS) {
      await sendForm({
        formId: `#${props.custom_id || 'formStayUpdated'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_NEWSLETTER,
      });
    }
  };

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'formStayUpdated'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes, _loading && 'opacity-50')}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-stay-updated` : 'form-stay-updated'}
    >
      <CommonHidden name="formId" value="28" />
      <CommonHidden name="formType" value="email_newsletter" />
      <CommonHidden name="leadType" value="newsletter" />
      <CommonHidden name="leadSource" value="lamesarv.com" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge(
            'w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1 md:p-8 text-center justify-center',
            props.classes_header,
          )}
        >
          <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title || 'Stay Updated'}</div>
          <div className="text-md-medium leading-none">All fields marked with an asterisk (*) are required.</div>
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
          {!props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              Stay informed on new arrivals and best deals!
            </div>
          )}
        </div>
      )}
      <div
        className={twMerge(
          'w-full flex flex-col gap-5 p-1 md:p-8 bg-white md:border border-black-300 rounded-lg',
          props.classes_container,
        )}
      >
        {!props.extract_header && (
          <div className={twMerge('flex flex-col justify-center items-center gap-4', props.classes_header)}>
            <div className={twMerge('heading-h4 leading-none', props.classes_title)}>
              {props.title || 'Stay Updated'}
            </div>
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
            {!props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                Stay informed on new arrivals and best deals!
              </div>
            )}
          </div>
        )}
        <div className={twMerge('w-full flex flex-col gap-5 mx-auto', props.classes_content)}>
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-6">
            <div className="flex flex-col gap-2 md:flex-1">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name *"
                name="firstName"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
            </div>
            <div className="flex flex-col gap-2 md:flex-1">
              <CommonInput
                type="text"
                label="Last Name"
                placeholder="Last Name *"
                name="lastName"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <CommonMaskInput
              type="tel"
              mask="(999) 999-9999"
              label="Phone"
              placeholder="Phone"
              name="phone"
              classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
              hideLabel
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="w-full relative flex flex-row items-center">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="absolute left-0 top-2.5 md:top-4 size-4 shrink-0 md:size-5"
              />

              <CommonInput
                type="email"
                label="Email"
                placeholder="Email *"
                name="email"
                classContainer="w-full"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 pl-8 pr-16 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
              <BaseSubmit
                {...props}
                formNameSlug="form-stay-updated"
                styleClasses="absolute right-0 top-2.5 md:top-4 text-sm md:text-base font-bold disabled:opacity-50"
              />
            </div>
            {props.display_sms_checkbox && (
              <div className="flex flex-row py-2">
                <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
              </div>
            )}
          </div>
          {wasSuccessful && (
            <span
              className="text-xs md:text-sm mt-10 md:mt-5 text-center"
              data-tag-group="form-success-label"
              data-tag={
                props.data_tag
                  ? `${props.data_tag}--form-stay-updated-success-label`
                  : 'form-stay-updated-success-label'
              }
            >
              Thank you for Signing Up!
            </span>
          )}
        </div>
      </div>
    </CommonForm>
  );
});
